import { PageController } from '@client/components/_core/layout/PageController';
import { useTrpc } from '@client/hooks/api/useTrpc';
import type { RouterOutputs } from '@client/trpc/client';
import { createFileRoute } from '@tanstack/react-router';
import { Card, Spin } from 'antd';
import { useEffect, useRef } from 'react';

export const Route = createFileRoute('/console/$orgId/configuration/financials/billing/usage')({
    component: Usage,
});

type _Usage = RouterOutputs['billing']['getUsageByDate'];

function Usage() {
    const { trpc } = useTrpc();
    const chartRef = useRef<HTMLDivElement>(null);

    // Setup transactions query
    const { data: transactionsData, isLoading: transactionsLoading } = trpc.billing.getUsageByDate.useQuery({
        days: 30,
    });

    // TODO: use the Chart component (@client/components/_ui/data-display/Chart)
    useEffect(() => {
        let cancelled = false;
        let destroyChart: (() => void) | undefined;

        const init = async () => {
            if (!chartRef.current || !transactionsData || transactionsLoading) return;

            const { Chart } = await import('@antv/g2');
            if (cancelled || !chartRef.current) return;

            // Clear previous chart instance
            chartRef.current.innerHTML = '';

            const chart = new Chart({
                container: chartRef.current,
                autoFit: true,
                height: 400,
            });

            chart
                .interval()
                .data(transactionsData)
                .encode('x', 'date')
                .encode('y', 'amount')
                .axis('y', {
                    title: 'Amount ($)',
                    grid: true,
                })
                .axis('x', {
                    title: 'Date',
                    tickCount: 7,
                })
                .style('maxWidth', 20)
                .animate('enter', { type: 'fadeIn' });

            await chart.render();

            destroyChart = () => {
                chart.destroy();
            };
        };

        void init();

        return () => {
            cancelled = true;
            destroyChart?.();
        };
    }, [transactionsData, transactionsLoading]);

    return (
        <PageController route={Route}>
            <Card title="Usage in last 30 days">
                {transactionsLoading ? (
                    <div className="flex h-[400px] items-center justify-center">
                        <Spin size="large" />
                    </div>
                ) : (
                    <div ref={chartRef} />
                )}
            </Card>
        </PageController>
    );
}
